/**
 * VariablesEditor — Template-scoped modal for declaring workflow variables.
 *
 * Variables live on the template (NOT on individual nodes), so this editor is
 * driven purely by the builderStore variables slice — it never touches
 * node selection or node config state. Each variable is shaped
 * {name, type, description, required} with type ∈ {string, bool, file},
 * matching the WorkflowStarter dynamic-form consumer contract and the backend
 * WorkflowVariable Pydantic schema.
 */
import React from 'react';
import useBuilderStore from './store/builderStore';

// The type control offers exactly these three values (WorkflowStarter contract).
const VARIABLE_TYPES = ['string', 'bool', 'file'];

const panelStyle = {
    background: '#1E293B',
    borderRadius: '12px',
    width: '640px',
    maxWidth: '92vw',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.08)',
};

const inputStyle = {
    background: '#0F172A',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    padding: '8px 12px',
    color: '#F1F5F9',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
};

const labelStyle = {
    color: '#94A3B8',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    display: 'block',
    marginBottom: '4px',
};

const VariablesEditor = ({ isOpen, onClose }) => {
    const variables = useBuilderStore((s) => s.variables);
    const addVariable = useBuilderStore((s) => s.addVariable);
    const updateVariable = useBuilderStore((s) => s.updateVariable);
    const removeVariable = useBuilderStore((s) => s.removeVariable);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9998,
                background: 'rgba(2,6,23,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
            }}
            onClick={onClose}
        >
            <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h3 style={{ color: '#F1F5F9', fontSize: '15px', fontWeight: 600, margin: 0 }}>
                            Template Variables
                        </h3>
                        <p style={{ color: '#94A3B8', fontSize: '12px', margin: '4px 0 0' }}>
                            Declare the inputs collected when this workflow is started.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '18px', padding: '4px' }}
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {variables.length === 0 && (
                        <div style={{ color: '#64748B', fontSize: '13px', textAlign: 'center', padding: '24px 0' }}>
                            No variables yet. Add one to collect input when the workflow starts.
                        </div>
                    )}

                    {variables.map((variable, index) => (
                        <div
                            key={variable._uid ?? index}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1.2fr 0.9fr 1.4fr auto auto',
                                gap: '10px',
                                alignItems: 'end',
                                background: '#0F172A',
                                border: '1px solid rgba(255,255,255,0.06)',
                                borderRadius: '10px',
                                padding: '12px',
                            }}
                        >
                            {/* Name */}
                            <div>
                                <label style={labelStyle}>Name</label>
                                <input
                                    value={variable.name || ''}
                                    onChange={(e) => updateVariable(index, { name: e.target.value })}
                                    placeholder="e.g. client_name"
                                    style={inputStyle}
                                />
                            </div>

                            {/* Type */}
                            <div>
                                <label style={labelStyle}>Type</label>
                                <select
                                    value={variable.type || 'string'}
                                    onChange={(e) => updateVariable(index, { type: e.target.value })}
                                    style={inputStyle}
                                >
                                    {VARIABLE_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Description */}
                            <div>
                                <label style={labelStyle}>Description</label>
                                <input
                                    value={variable.description || ''}
                                    onChange={(e) => updateVariable(index, { description: e.target.value })}
                                    placeholder="Shown as the field hint"
                                    style={inputStyle}
                                />
                            </div>

                            {/* Required */}
                            <div style={{ paddingBottom: '8px' }}>
                                <label style={labelStyle}>Required</label>
                                <input
                                    type="checkbox"
                                    checked={!!variable.required}
                                    onChange={(e) => updateVariable(index, { required: e.target.checked })}
                                    style={{ width: '18px', height: '18px', accentColor: '#6366F1', cursor: 'pointer' }}
                                />
                            </div>

                            {/* Remove */}
                            <div style={{ paddingBottom: '4px' }}>
                                <button
                                    onClick={() => removeVariable(index)}
                                    title="Remove variable"
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        color: '#EF4444',
                                        borderRadius: '8px',
                                        padding: '8px 10px',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <button
                        onClick={() => addVariable({ name: '', type: 'string', description: '', required: true })}
                        style={{
                            padding: '9px 16px',
                            borderRadius: '8px',
                            border: '1px solid #6366F155',
                            background: 'transparent',
                            color: '#6366F1',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        + Add Variable
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '9px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#6366F1',
                            color: 'white',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VariablesEditor;
